import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * WAM-457 — the AWS WAF JavaScript integration SDK for the sign-up wizard.
 *
 * WAM-455 put a `PublicSignupChallenge` rule on `/public/signup/*` (see
 * `nest/cdk/lib/waf.ts`), but the WAF sits in front of the API's ALB
 * (`api[.staging].weaveaccreditation.com`) while the SPA is served from
 * S3/CloudFront on a different origin (`app.weaveaccreditation.com` /
 * `w4u.sitefpo.com`) — so the WAF only ever sees the XHR, never a document
 * request it could challenge with an interstitial page. The integration SDK
 * runs IN the SPA, solves the challenge in the background, and hands back a
 * token this service attaches to outgoing signup requests as the
 * `x-aws-waf-token` header — the documented pattern for a protected API that
 * lives on a different domain than the application
 * (https://docs.aws.amazon.com/waf/latest/developerguide/waf-application-integration-js.html
 * — cross-domain integration).
 *
 * The script tag AWS's console hands you for a web ACL is environment-specific
 * (it's generated per web ACL, one per env — see `environment.wafIntegrationUrl`
 * in the `environments/` files), so it is loaded here rather than pinned in
 * `index.html`, and only lazily: nothing outside the sign-up wizard should pay
 * for it.
 *
 * Failure modes are deliberately non-fatal — this SDK is a bot-mitigation
 * layer, not an auth mechanism, and the wizard must never hang or dead-end
 * because a script failed to load (ad-blockers, a slow/offline network, or a
 * WAF outage all count):
 *   - script load error, or no `window.AwsWafIntegration` after
 *     {@link LOAD_TIMEOUT_MS} → resolves `null`, caller sends the request with
 *     no token (staging then answers with a WAF challenge response — a visible
 *     but recoverable failure, not a hang; production stays in `count` mode
 *     until WAM-457 is verified there, so a missing token there is inert).
 *   - `getToken()` itself hangs or throws → same `null` fallback, on its own
 *     timeout, so one bad SDK call can't wedge the signup submit button.
 */
@Injectable({ providedIn: 'root' })
export class WafIntegrationService {
  private static readonly LOAD_TIMEOUT_MS = 5000;
  private static readonly TOKEN_TIMEOUT_MS = 3000;

  private loadPromise: Promise<boolean> | null = null;

  /**
   * Resolves a fresh WAF token, or `null` if the SDK is unavailable/unconfigured
   * or times out. Never rejects.
   */
  getToken(): Observable<string | null> {
    if (!environment.wafIntegrationUrl) return of(null);
    return new Observable<string | null>((subscriber) => {
      let settled = false;
      const finish = (value: string | null) => {
        if (settled) return;
        settled = true;
        subscriber.next(value);
        subscriber.complete();
      };

      this.ensureScriptLoaded()
        .then((ready) => {
          if (!ready) return finish(null);
          const sdk = (window as any).AwsWafIntegration;
          if (!sdk?.getToken) return finish(null);

          const timeoutId = setTimeout(() => finish(null), WafIntegrationService.TOKEN_TIMEOUT_MS);
          Promise.resolve(sdk.getToken())
            .then((token: unknown) => {
              clearTimeout(timeoutId);
              finish(typeof token === 'string' && token ? token : null);
            })
            .catch(() => {
              clearTimeout(timeoutId);
              finish(null);
            });
        })
        .catch(() => finish(null));
    });
  }

  /** Injects the integration script once and resolves once the SDK global is present. */
  private ensureScriptLoaded(): Promise<boolean> {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise<boolean>((resolve) => {
      if ((window as any).AwsWafIntegration?.getToken) {
        resolve(true);
        return;
      }

      const timeoutId = setTimeout(() => resolve(false), WafIntegrationService.LOAD_TIMEOUT_MS);

      const script = document.createElement('script');
      script.src = environment.wafIntegrationUrl;
      script.async = true;
      script.onload = () => {
        clearTimeout(timeoutId);
        // The integration script defines window.AwsWafIntegration synchronously
        // on execution — no ready callback to wait on.
        resolve(!!(window as any).AwsWafIntegration?.getToken);
      };
      script.onerror = () => {
        clearTimeout(timeoutId);
        resolve(false);
      };
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }
}
